import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useResellerDetail, useDeleteReseller } from '@/api/resellers';
import { DetailHeader } from '@/components/detail-header';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { resellerStatusTone, humanize } from '../components/reseller-tones';
import { ResellerOverviewTab } from './reseller-overview-tab';
import { ResellerCustomersTab } from './reseller-customers-tab';
import { ResellerServicesTab } from './reseller-services-tab';
import { ResellerInvoicesTab } from './reseller-invoices-tab';

const TABS = [
  { value: 'overview',  label: 'Overview' },
  { value: 'customers', label: 'Customers' },
  { value: 'services',  label: 'Services' },
  { value: 'invoices',  label: 'Invoices' },
] as const;

const TAB_VALUES = TABS.map((t) => t.value) as readonly string[];

/**
 * `/resellers/:id` — reseller detail page. DetailHeader (breadcrumb + title +
 * status pill) over four tabs: Overview (info + stats), Customers (linked with
 * add/remove), Services, Invoices. Active tab persisted in `?tab=`.
 */
export default function ResellerDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && TAB_VALUES.includes(requestedTab) ? requestedTab : 'overview';

  const { data, isLoading, isError, refetch } = useResellerDetail(id);
  const reseller = data?.result;
  const del = useDeleteReseller();

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const handleDelete = async () => {
    setDeleteError('');
    try {
      await del.mutateAsync(id);
      toast.success('Reseller deleted');
      navigate('/resellers');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to delete reseller.';
      setDeleteError(msg);
      setConfirmDelete(false);
    }
  };

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load reseller" onRetry={() => refetch()} />
      ) : isLoading || !reseller ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <DetailHeader
            parent={{ label: 'Resellers', to: '/resellers' }}
            current={reseller.reseller_name}
            idPrefix={`#${reseller.reseller_id}`}
            title={reseller.reseller_name}
            meta={
              <StatusPill tone={resellerStatusTone(reseller.reseller_status)}>
                {humanize(reseller.reseller_status)}
              </StatusPill>
            }
            actions={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton variant="ghost" aria-label="Reseller actions">
                    <MoreHorizontal size={16} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem tone="danger" onSelect={() => setConfirmDelete(true)}>
                    <Trash2 size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                    Delete reseller
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />

          {deleteError ? <Alert tone="danger" title={deleteError} /> : null}

          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview">
              <ResellerOverviewTab reseller={reseller} onRefetch={() => refetch()} />
            </TabsContent>

            <TabsContent value="customers">
              {activeTab === 'customers' ? (
                <ResellerCustomersTab resellerId={id} />
              ) : null}
            </TabsContent>

            <TabsContent value="services">
              {activeTab === 'services' ? (
                <ResellerServicesTab resellerId={id} />
              ) : null}
            </TabsContent>

            <TabsContent value="invoices">
              {activeTab === 'invoices' ? (
                <ResellerInvoicesTab resellerId={id} />
              ) : null}
            </TabsContent>
          </Tabs>

          <ConfirmDialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title="Delete reseller?"
            description={`"${reseller.reseller_name}" will be permanently removed. This action cannot be undone.`}
            confirmLabel="Delete"
            destructive
            loading={del.isPending}
            onConfirm={handleDelete}
          />
        </div>
      )}
    </Container>
  );
}
